import axios, { AxiosError, AxiosInstance } from 'axios';
import config from '@/config/config';
import { flightRouteFromGeoJson } from '@/utils/flightRouteGeoJson';
import type {
  NotamGeoJson,
  FlightPathRequest,
  FlightRoute,
  ContextualRequest,
  ContextualResponse
} from '@/types/notam';

/**
 * Per-field detail for an unknown ICAO returned by the server in 404 bodies
 * from /api/notam-geojson* endpoints (#18).
 */
export interface UnknownAirportDetail {
  icao: string;
  suggestions: string[];
}

/**
 * Thrown by ApiClient when the server returns 404 with an `unknown` body
 * field (one or more ICAOs not in the airport DB). Carries the unknown
 * ICAO(s) + did-you-mean suggestions so the SPA can render an actionable
 * error banner.
 */
export class UnknownAirportError extends Error {
  public readonly unknown: Record<string, UnknownAirportDetail>;

  constructor(unknown: Record<string, UnknownAirportDetail>, detail: string) {
    super(detail);
    this.name = 'UnknownAirportError';
    this.unknown = unknown;
  }
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Response interceptor: convert 404 with `unknown` body into a typed
    // UnknownAirportError so callers can branch on it (#18). The former
    // 429 → /quota-exceeded branch is gone with the #16 server gate (#58):
    // the freemium gate is client-driven via useUsageGate, and a 429 is
    // now just an error the caller's retry/backoff logic handles.
    this.client.interceptors.response.use(
      response => response,
      (error: AxiosError<{
        detail?: string;
        unknown?: Record<string, UnknownAirportDetail>;
      }>) => {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 404 && data?.unknown) {
          return Promise.reject(
            new UnknownAirportError(data.unknown, data.detail ?? 'Airport not found')
          );
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch NOTAMs for a single airport
   */
  async getSingleAirportNotams(airport: string, lang: string = 'en'): Promise<NotamGeoJson> {
    const response = await this.client.get<NotamGeoJson>(
      `${config.api.endpoints.singleAirport}`,
      {
        params: { airport, lang }
      }
    );
    return response.data;
  }

  /**
   * Fetch airport NOTAMs for a flight path
   */
  async getFlightPathAirportNotams(
    request: FlightPathRequest,
    lang: string = 'en'
  ): Promise<NotamGeoJson> {
    const params: Record<string, string | number> = {
      departure: request.departure,
      destination: request.destination,
      lang
    };

    if (request.alternate1) params.alternate1 = request.alternate1;
    if (request.alternate2) params.alternate2 = request.alternate2;

    const response = await this.client.get<NotamGeoJson>(
      config.api.endpoints.airportsGeoJson,
      { params }
    );
    return response.data;
  }

  /**
   * Fetch en-route NOTAMs for a flight path (includes alternate routes)
   */
  async getFlightPathEnrouteNotams(
    request: FlightPathRequest,
    lang: string = 'en'
  ): Promise<NotamGeoJson> {
    const params: Record<string, string | number> = {
      departure: request.departure,
      destination: request.destination,
      buffer: request.buffer,
      lang
    };

    // Include alternates for en-route NOTAMs along destination→alternate routes
    if (request.alternate1) params.alternate1 = request.alternate1;
    if (request.alternate2) params.alternate2 = request.alternate2;

    const response = await this.client.get<NotamGeoJson>(
      config.api.endpoints.enrouteGeoJson,
      { params }
    );
    return response.data;
  }

  /**
   * Fetch NOTAMs near a GPS location
   */
  async getNearbyNotams(
    lat: number,
    lng: number,
    radiusNm: number = 25,
    lang: string = 'en'
  ): Promise<NotamGeoJson> {
    const response = await this.client.get<NotamGeoJson>(
      config.api.endpoints.nearby,
      {
        params: { lat, lng, radius: radiusNm, lang }
      }
    );
    return response.data;
  }

  /**
   * Resolve two ICAO codes to their coordinates (#451).
   *
   * Reuses the airport briefing endpoint, which already carries a
   * `flightRoute` LineString whose first and last coordinates ARE the two
   * airports, and already 404s with a did-you-mean (#18) that the
   * interceptor above turns into an `UnknownAirportError`.
   *
   * The cost is stated plainly on the issue: a briefing-sized response
   * (~120 KB for LFPG → LFBO) read for four numbers. If that stops being
   * acceptable, the replacement is a dedicated lookup endpoint behind this
   * one method — which is why the simulator calls this and not the
   * briefing method.
   *
   * `signal` aborts an in-flight lookup, so a code being corrected keystroke
   * by keystroke cannot have an older response land after a newer one.
   *
   * `lang` is passed like every other caller of this endpoint even though
   * the NOTAM text is discarded: a request that asks for a translation set
   * it does not want is a request nobody can reason about from the server
   * side.
   */
  async getRouteCoordinates(
    departure: string,
    destination: string,
    signal?: AbortSignal,
    lang: string = 'en'
  ): Promise<FlightRoute> {
    const response = await this.client.get<NotamGeoJson>(
      config.api.endpoints.airportsGeoJson,
      { params: { departure, destination, lang }, signal }
    );

    // Shared with the briefing screen, which reads the same line out of the
    // same response — two copies of one server contract had already drifted.
    const route = flightRouteFromGeoJson(response.data, departure, destination);

    // A 200 that carries no usable route line is a server the client cannot
    // use. Failing here keeps the caller out of a half-armed state, which is
    // the one thing the simulator must never be.
    if (route === null) {
      throw new Error('Route coordinates missing from the airports response');
    }
    return route;
  }

  /**
   * Fetch contextual proximity NOTAMs (#8).
   *
   * Stateless snapshot: caller maintains the previous result set and computes
   * diffs (entry/exit events) client-side. The internal SPA-bundled API key
   * is read from VITE_INTERNAL_API_KEY at build time.
   */
  async getContextualNotams(
    request: ContextualRequest,
    signal?: AbortSignal
  ): Promise<ContextualResponse> {
    const internalKey = import.meta.env.VITE_INTERNAL_API_KEY;
    if (!internalKey) {
      throw new Error('VITE_INTERNAL_API_KEY missing at build time; cannot call /api/contextualNotams');
    }
    const response = await this.client.post<ContextualResponse>(
      config.api.endpoints.contextualNotams,
      request,
      {
        headers: {
          Authorization: `Bearer ${internalKey}`
        },
        signal
      }
    );
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
